// Haptic feedback utility for mobile devices
// Uses the Vibration API where available

type HapticType = 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error';

const hapticPatterns: Record<HapticType, number | number[]> = {
  light: 10,
  medium: 25,
  heavy: 50,
  success: [10, 50, 10],
  warning: [30, 30, 30],
  error: [50, 100, 50],
};

/**
 * Triggers haptic feedback on supported devices
 * Falls back gracefully on unsupported devices
 */
export function triggerHaptic(type: HapticType = 'light'): void {
  // Check if Vibration API is available
  if (!('vibrate' in navigator)) {
    return;
  }

  try {
    const pattern = hapticPatterns[type];
    navigator.vibrate(pattern);
  } catch (error) {
    // Silently fail - haptics are nice-to-have, not critical
    console.debug('Haptic feedback unavailable:', error);
  }
}

/**
 * Check if haptic feedback is supported
 */
export function isHapticSupported(): boolean {
  return 'vibrate' in navigator;
}

/**
 * Trigger haptic on button click - can be used as onClick handler wrapper
 */
export function withHaptic<T extends (...args: any[]) => any>(
  fn: T,
  type: HapticType = 'light'
): (...args: Parameters<T>) => ReturnType<T> {
  return (...args: Parameters<T>): ReturnType<T> => {
    triggerHaptic(type);
    return fn(...args);
  };
}
